import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { FileText, ArrowRight, Loader2, AlertCircle, ExternalLink } from 'lucide-react';
import { api, formatApiError } from '../../lib/api';
import { useAdminTheme, tc } from './theme';

export default function AdminV2Pages() {
  const { theme } = useAdminTheme();
  const [pages, setPages] = useState([]);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    api
      .get('/api/admin-v2/pages')
      .then((res) => {
        if (cancelled) return;
        setPages(res.data || []);
      })
      .catch((err) => !cancelled && setError(formatApiError(err)))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <div className="space-y-6">
      <div>
        <p className={`text-xs uppercase tracking-widest font-bold ${theme === 'light' ? 'text-fuchsia-600' : 'text-fuchsia-300/80'}`}>Admin v2</p>
        <h1 className={`text-3xl font-extrabold mt-1 ${tc.textPrimary(theme)}`}>Pages</h1>
        <p className={`mt-2 max-w-2xl ${tc.textMuted(theme)}`}>
          Every public page lives here. Click a page to edit its sections (Hero, Features,
          Pricing, FAQs…) and SEO metadata. Each section ships with{' '}
          <span className={theme === 'light' ? 'text-fuchsia-600' : 'text-fuchsia-300'}>AI Generate</span> and{' '}
          <span className={theme === 'light' ? 'text-fuchsia-600' : 'text-fuchsia-300'}>Improve</span> buttons.
        </p>
      </div>

      {error && (
        <div className={`flex items-start gap-2 rounded-xl px-4 py-3 text-sm ${theme === 'light' ? 'text-rose-700 bg-rose-50 border border-rose-200' : 'text-rose-300 bg-rose-500/10 border border-rose-500/30'}`}>
          <AlertCircle size={16} className="mt-0.5 shrink-0" />
          <span>{error}</span>
        </div>
      )}

      {loading && !error && (
        <div className={`flex items-center gap-2 text-sm ${tc.textMuted(theme)}`}>
          <Loader2 size={16} className="animate-spin" /> Loading pages…
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {pages.map((page) => (
          <div
            key={page.id || page.slug}
            className={`group relative rounded-2xl hover:border-violet-500/40 p-5 transition-all ${tc.cardBg(theme)}`}
          >
            <div className="flex items-start justify-between">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-violet-500 to-fuchsia-500 flex items-center justify-center text-white shadow-lg shadow-violet-500/30">
                <FileText size={18} />
              </div>
              <span
                className={`text-[10px] uppercase tracking-widest font-bold px-2 py-0.5 rounded-md ${
                  page.enabled
                    ? (theme === 'light' ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' : 'bg-emerald-500/15 text-emerald-300 border border-emerald-500/30')
                    : (theme === 'light' ? 'bg-slate-100 text-slate-600 border border-slate-200' : 'bg-slate-500/15 text-slate-400 border border-slate-500/30')
                }`}
              >
                {page.enabled ? 'enabled' : 'disabled'}
              </span>
            </div>
            <h3 className={`text-lg font-bold mt-3 ${tc.textPrimary(theme)}`}>{page.name}</h3>
            <p className={`text-xs font-mono mt-1 ${tc.textDim(theme)}`}>{page.public_path}</p>
            <p className={`text-xs mt-3 ${tc.textMuted(theme)}`}>
              {page.sections?.length || 0} section{(page.sections?.length || 0) === 1 ? '' : 's'} ·{' '}
              <span className={theme === 'light' ? 'text-fuchsia-600' : 'text-fuchsia-300'}>{page.slug}</span>
            </p>
            <div className="mt-4 flex items-center gap-3 text-sm">
              <Link
                to={`/admin/pages/${page.slug}`}
                className={`inline-flex items-center gap-1 font-semibold ${theme === 'light' ? 'text-fuchsia-600 hover:text-fuchsia-700' : 'text-fuchsia-300 hover:text-fuchsia-200'}`}
              >
                Edit <ArrowRight size={14} />
              </Link>
              <a
                href={page.public_path}
                target="_blank"
                rel="noreferrer"
                className={`inline-flex items-center gap-1 text-xs ${tc.textMuted(theme)} hover:${tc.textPrimary(theme)}`}
              >
                <ExternalLink size={12} /> visit
              </a>
            </div>
          </div>
        ))}
      </div>

      {!loading && !error && pages.length === 0 && (
        <div className={`rounded-2xl border border-dashed p-10 text-center ${theme === 'light' ? 'border-slate-300 text-slate-500' : 'border-white/15 text-slate-400'}`}>
          No pages seeded yet. Restart the backend to re-run `seed_pages_v2`.
        </div>
      )}
    </div>
  );
}
